import { useCallback, useEffect, useRef, useState } from 'react';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * The admin story carries full attachments; the public share page projects them
 * down to a filename and a signed URL with no attachment id, because an id is
 * not a reader's business. Accept the narrower shape so both surfaces run the
 * same strip rather than each keeping its own.
 */
export interface CarouselPhoto {
  id?: string;
  fileName: string;
  downloadUrl: string;
}

/** Stable within a photo set and across a URL refresh, unique on collisions. */
function keyFor(photo: CarouselPhoto, index: number): string {
  return `${photo.id ?? photo.fileName}#${index}`;
}

/**
 * The media strip on a review story.
 *
 * Every photo lives in ONE horizontal scroll-snap track that is exactly as wide
 * as its frame, so a single photo is visible at a time while a trackpad or
 * touch swipe still moves between them natively — no JS gesture handling. The
 * arrows and the dots drive the same track through `scrollTo`, and a scroll
 * listener syncs the index back when the user swipes instead of clicking, so
 * the counter stays honest whichever way the photo changed.
 *
 * Failures are tracked per photo, not for the strip: one dead signed URL must
 * not blank out the siblings that still resolve.
 */
export function StoryPhotoCarousel({
  photos,
  compact,
}: {
  photos: CarouselPhoto[];
  compact?: boolean;
}) {
  const trackRef = useRef<HTMLDivElement>(null);
  const [index, setIndex] = useState(0);
  const [failed, setFailed] = useState<Record<string, true>>({});
  const [loaded, setLoaded] = useState<Record<string, true>>({});

  const goTo = useCallback(
    (next: number) => {
      const clamped = Math.max(0, Math.min(next, photos.length - 1));
      setIndex(clamped);
      const track = trackRef.current;
      // Guarded: jsdom has no layout and therefore no scrollTo.
      if (track && typeof track.scrollTo === 'function') {
        track.scrollTo({ left: track.clientWidth * clamped, behavior: 'smooth' });
      }
    },
    [photos.length],
  );

  // A swipe moves the track without going through goTo, so read the index back
  // off the scroll position rather than letting the counter drift.
  // This component is NOT remounted per story: the map info window renders one
  // card at a fixed position with a changing story, and the drawer stays
  // mounted while its photos change. Without this the index survived the swap
  // and rendered "3/2" against a shorter set, with no dot active and the nav
  // starting from a stale position. Reset during render rather than in an
  // effect so the wrong count is never painted.
  // Two signatures, because the two kinds of state answer different questions.
  //
  // WHICH photos (ids) decides position. A different story starts at the first
  // photo; a refetch of the same story must not yank the viewer back to it.
  const identity = photos.map((photo, index) => keyFor(photo, index)).join('|');
  const [seenIdentity, setSeenIdentity] = useState(identity);
  if (identity !== seenIdentity) {
    setSeenIdentity(identity);
    setIndex(0);
  }

  // WHERE they load from decides load state. `downloadUrl` is a presigned S3
  // link with a 3600s TTL, so a refetch returns the same ids with fresh
  // signatures. Keyed on id alone, a photo that errored on an expired URL kept
  // its placeholder for the life of the mount and never retried the new one.
  const sources = photos
    .map((photo, index) => `${keyFor(photo, index)}\u0000${photo.downloadUrl}`)
    .join('|');
  const [seenSources, setSeenSources] = useState(sources);
  if (sources !== seenSources) {
    setSeenSources(sources);
    setFailed({});
    setLoaded({});
  }

  // The track's scroll offset is DOM state, so it needs the effect. Keyed to
  // identity: a URL refresh leaves the viewer where they were.
  useEffect(() => {
    const track = trackRef.current;
    if (track && typeof track.scrollTo === 'function') {
      track.scrollTo({ left: 0, behavior: 'auto' });
    }
  }, [identity]);

  const syncIndex = useCallback(() => {
    const track = trackRef.current;
    if (!track || track.clientWidth === 0) return;
    setIndex(Math.round(track.scrollLeft / track.clientWidth));
  }, []);

  if (photos.length === 0) return null;

  const many = photos.length > 1;

  return (
    <div className={cn('group/media relative overflow-hidden bg-muted', compact ? 'h-24' : 'h-40')}>
      <div
        ref={trackRef}
        onScroll={syncIndex}
        className={cn(
          'flex h-full w-full snap-x snap-mandatory overflow-x-auto overflow-y-hidden',
          // The native scrollbar would sit across the photo; the dots and
          // arrows are the affordance instead.
          '[scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        )}
      >
        {photos.map((photo, photoIndex) => (
          <div
            key={keyFor(photo, photoIndex)}
            className="relative h-full w-full shrink-0 snap-center"
          >
            {failed[keyFor(photo, photoIndex)] ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-1.5 px-4 text-muted-foreground">
                <ImageOff aria-hidden className="size-5" />
                <span className="line-clamp-1 text-[11px]">{photo.fileName}</span>
              </div>
            ) : (
              <img
                src={photo.downloadUrl}
                alt={photo.fileName}
                // Photos resolve in rather than snapping.
                className={cn(
                  'h-full w-full object-cover motion-safe:transition-[opacity,transform] motion-safe:duration-500',
                  'motion-safe:group-hover/media:scale-[1.04]',
                  loaded[keyFor(photo, photoIndex)] ? 'opacity-100' : 'opacity-0',
                )}
                // Lazy for ALL of them, including the lead. This strip is
                // mounted once per story, and the stories list and the public
                // share page render a whole page of cards — an eager lead photo
                // meant one immediate fetch per card, most of them below the
                // fold. A lazy image that IS in view is still fetched promptly,
                // so the visible card loses nothing.
                loading="lazy"
                onLoad={() =>
                  setLoaded((current) => ({ ...current, [keyFor(photo, photoIndex)]: true }))
                }
                onError={() =>
                  setFailed((current) => ({ ...current, [keyFor(photo, photoIndex)]: true }))
                }
              />
            )}
          </div>
        ))}
      </div>

      {/* Scrim: the counter and dots have to stay legible over a light photo. */}
      {many ? (
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 bottom-0 h-14 bg-gradient-to-t from-black/45 to-transparent"
        />
      ) : null}

      {many ? (
        <>
          <button
            type="button"
            aria-label="Previous photo"
            disabled={index === 0}
            onClick={(event) => {
              event.stopPropagation();
              goTo(index - 1);
            }}
            className={cn(
              'absolute left-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-1 text-white',
              'opacity-0 transition-opacity duration-200 focus-visible:opacity-100',
              'group-hover/media:opacity-100 disabled:pointer-events-none disabled:opacity-0',
            )}
          >
            <ChevronLeft className="size-4" />
          </button>
          <button
            type="button"
            aria-label="Next photo"
            disabled={index === photos.length - 1}
            onClick={(event) => {
              event.stopPropagation();
              goTo(index + 1);
            }}
            className={cn(
              'absolute right-2 top-1/2 -translate-y-1/2 rounded-full bg-black/45 p-1 text-white',
              'opacity-0 transition-opacity duration-200 focus-visible:opacity-100',
              'group-hover/media:opacity-100 disabled:pointer-events-none disabled:opacity-0',
            )}
          >
            <ChevronRight className="size-4" />
          </button>

          <span className="absolute bottom-2 right-2 rounded-full bg-black/55 px-2 py-0.5 text-[10px] font-medium text-white">
            {index + 1}/{photos.length}
          </span>

          <div className="absolute bottom-2 left-1/2 flex -translate-x-1/2 items-center gap-1">
            {photos.map((photo, dotIndex) => (
              <button
                key={keyFor(photo, dotIndex)}
                type="button"
                aria-label={`Go to photo ${dotIndex + 1}`}
                onClick={(event) => {
                  event.stopPropagation();
                  goTo(dotIndex);
                }}
                className={cn(
                  'h-1.5 rounded-full transition-all duration-200',
                  dotIndex === index ? 'w-4 bg-white' : 'w-1.5 bg-white/55 hover:bg-white/80',
                )}
              />
            ))}
          </div>
        </>
      ) : null}
    </div>
  );
}
